import React, { useMemo, useState, useCallback, useRef } from 'react';
import Plot from 'react-plotly.js';
import { useData } from '../../context/DataContext';
import { disperseAnnotations } from '../../utils/plotUtils';

const QUADRANT_COLORS = {
  DOWN_not_reversed: '#2471a3',
  UP_not_reversed: '#c0392b',
  DOWN_then_UP: '#27ae60',
  UP_then_DOWN: '#e67e22',
};

const QUADRANT_LABELS = {
  DOWN_not_reversed: 'Down (insensitive)',
  UP_not_reversed: 'Up (insensitive)',
  DOWN_then_UP: 'Down \u2192 reversed (sensitive)',
  UP_then_DOWN: 'Up \u2192 reversed (sensitive)',
};

const FDR_OPTIONS = [0.001, 0.01, 0.05, 0.1];

const SIGNATURES = [
  { panel: 'GCB-DLBCL Biology', label: 'GCB-DLBCL', color: '#c0392b' },
  { panel: 'GC Polarization', label: 'GC Polar.', color: '#2471a3' },
  { panel: 'Immune Evasion', label: 'Immune', color: '#27ae60' },
];

export default function QuadrantPlot({ sensitivity, onSelectGene }) {
  const { data, resolveGene } = useData();
  const [fcCutoff, setFcCutoff] = useState(1);
  const [fdrCutoff, setFdrCutoff] = useState(0.05);
  const [toggledLabels, setToggledLabels] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [activeSignature, setActiveSignature] = useState(null);
  const searchRef = useRef(null);

  const signatureGenes = useMemo(() => {
    if (!activeSignature || !data?.panels) return [];
    const panel = data.panels[activeSignature.panel];
    if (!panel) return [];
    const genes = [];
    for (const subcatGenes of Object.values(panel)) {
      genes.push(...subcatGenes);
    }
    return genes;
  }, [activeSignature, data]);

  const plotData = useMemo(() => {
    if (!sensitivity) return null;

    const genes = Object.keys(sensitivity);
    const quadrants = {};

    for (const [gene, d] of Object.entries(sensitivity)) {
      const quad = d.quad;
      if (!quadrants[quad]) quadrants[quad] = { genes: [], x: [], y: [], hover: [] };

      const passFC = Math.abs(d.tFC) >= fcCutoff || Math.abs(d.eFC) >= fcCutoff;
      const passFDR = d.tFDR <= fdrCutoff || d.eFDR <= fdrCutoff;

      if (!passFC && !passFDR) {
        if (!quadrants._ns) quadrants._ns = { genes: [], x: [], y: [], hover: [] };
        quadrants._ns.genes.push(gene);
        quadrants._ns.x.push(d.tFC);
        quadrants._ns.y.push(d.eFC);
        quadrants._ns.hover.push(gene);
        continue;
      }

      quadrants[quad].genes.push(gene);
      quadrants[quad].x.push(d.tFC);
      quadrants[quad].y.push(d.eFC);
      quadrants[quad].hover.push(
        `<b>${gene}</b><br>Transform FC: ${d.tFC?.toFixed(2)}<br>EZH2i FC: ${d.eFC?.toFixed(2)}<br>Transform FDR: ${d.tFDR?.toExponential(2)}<br>EZH2i FDR: ${d.eFDR?.toExponential(2)}<br>${QUADRANT_LABELS[quad] || quad}`
      );
    }

    // Top genes by combined significance
    const scored = genes.map(g => {
      const d = sensitivity[g];
      const tSig = d.tFDR > 0 ? -Math.log10(d.tFDR) : 20;
      const eSig = d.eFDR > 0 ? -Math.log10(d.eFDR) : 20;
      return { gene: g, score: (tSig + eSig) * (Math.abs(d.tFC) + Math.abs(d.eFC)) };
    });
    scored.sort((a, b) => b.score - a.score);
    const topGenes = scored.slice(0, 15);

    return { quadrants, topGenes, allGenes: genes };
  }, [sensitivity, fcCutoff, fdrCutoff]);

  const handleSearchInput = useCallback((e) => {
    const q = e.target.value;
    setSearchQuery(q);
    setSearchResult(null);
    if (q.length >= 2 && data?.geneList) {
      const lower = q.toLowerCase();
      const matches = data.geneList.filter(g => g.toLowerCase().startsWith(lower)).slice(0, 8);
      setSuggestions(matches);
    } else {
      setSuggestions([]);
    }
  }, [data]);

  const doSearch = useCallback((geneName) => {
    const resolved = resolveGene(geneName);
    if (resolved && sensitivity[resolved]) {
      setSearchResult({ gene: resolved, found: true });
      setSearchQuery(resolved);
    } else if (resolved) {
      setSearchResult({ gene: resolved, found: false });
      setSearchQuery(resolved);
    } else {
      setSearchResult({ gene: geneName, found: false });
    }
    setSuggestions([]);
  }, [resolveGene, sensitivity]);

  const handleSearchKey = useCallback((e) => {
    if (e.key === 'Enter') doSearch(searchQuery);
  }, [searchQuery, doSearch]);

  const handleSuggestionClick = useCallback((gene) => {
    doSearch(gene);
    searchRef.current?.blur();
  }, [doSearch]);

  const handleClick = useCallback((e) => {
    if (e.points && e.points[0]) {
      const gene = e.points[0].text || e.points[0].hovertext;
      const cleanGene = gene?.replace(/<[^>]+>/g, '').split('\n')[0].trim();
      if (cleanGene && sensitivity[cleanGene]) {
        setToggledLabels(prev => {
          const next = new Set(prev);
          if (next.has(cleanGene)) {
            next.delete(cleanGene);
            if (onSelectGene) onSelectGene(cleanGene);
          } else {
            next.add(cleanGene);
          }
          return next;
        });
      }
    }
  }, [onSelectGene, sensitivity]);

  if (!plotData) return <div className="plot-placeholder">Loading quadrant data...</div>;

  const { quadrants, topGenes } = plotData;
  const plotWidth = 880;
  const plotHeight = 640;
  const margin = { l: 70, r: 30, t: 50, b: 70 };

  const traces = [];

  // NS points (below cutoffs)
  if (quadrants._ns) {
    let nsData = quadrants._ns;
    if (nsData.x.length > 3000) {
      const step = Math.ceil(nsData.x.length / 3000);
      const idx = nsData.x.map((_, i) => i).filter((_, i) => i % step === 0);
      nsData = {
        genes: idx.map(i => nsData.genes[i]),
        x: idx.map(i => nsData.x[i]),
        y: idx.map(i => nsData.y[i]),
        hover: idx.map(i => nsData.hover[i]),
      };
    }
    traces.push({
      type: 'scattergl', mode: 'markers',
      x: nsData.x, y: nsData.y,
      marker: { size: 2, color: '#666', opacity: 0.3 },
      hovertext: nsData.hover, hoverinfo: 'text',
      name: `NS (${quadrants._ns.x.length})`, showlegend: true,
    });
  }

  // Quadrant traces
  for (const [quad, color] of Object.entries(QUADRANT_COLORS)) {
    const qd = quadrants[quad];
    if (!qd || qd.x.length === 0) continue;
    traces.push({
      type: 'scattergl', mode: 'markers',
      x: qd.x, y: qd.y,
      marker: { size: 5, color, opacity: 0.7 },
      hovertext: qd.hover, hoverinfo: 'text',
      name: `${QUADRANT_LABELS[quad]} (${qd.x.length})`,
      text: qd.genes,
    });
  }

  // Signature highlight
  if (activeSignature && signatureGenes.length > 0) {
    const hx = [], hy = [], htext = [];
    for (const g of signatureGenes) {
      const d = sensitivity[g];
      if (d) {
        hx.push(d.tFC);
        hy.push(d.eFC);
        htext.push(`<b>${g}</b><br>Transform: ${d.tFC?.toFixed(2)}<br>EZH2i: ${d.eFC?.toFixed(2)}`);
      }
    }
    if (hx.length > 0) {
      traces.push({
        type: 'scatter', mode: 'markers',
        x: hx, y: hy,
        marker: { size: 9, color: activeSignature.color, opacity: 0.9, line: { width: 1.5, color: '#fff' } },
        hovertext: htext, hoverinfo: 'text',
        name: activeSignature.label,
      });
    }
  }

  // Search result
  if (searchResult?.found) {
    const d = sensitivity[searchResult.gene];
    if (d) {
      traces.push({
        type: 'scatter', mode: 'markers',
        x: [d.tFC], y: [d.eFC],
        marker: { size: 14, color: '#f1c40f', symbol: 'diamond', line: { width: 2, color: '#1a1a1a' } },
        hovertext: [`<b>${searchResult.gene}</b><br>Transform: ${d.tFC?.toFixed(2)}<br>EZH2i: ${d.eFC?.toFixed(2)}`],
        hoverinfo: 'text', name: searchResult.gene, showlegend: false,
      });
    }
  }

  // Build annotations
  const allLabeled = new Set(topGenes.map(t => t.gene));
  toggledLabels.forEach(g => allLabeled.add(g));
  if (searchResult?.found) allLabeled.add(searchResult.gene);
  if (activeSignature) signatureGenes.forEach(g => { if (sensitivity[g]) allLabeled.add(g); });

  // Compute axis domains
  const allX = [], allY = [];
  for (const qd of Object.values(quadrants)) {
    allX.push(...qd.x);
    allY.push(...qd.y);
  }
  const xPad = (Math.max(...allX) - Math.min(...allX)) * 0.05 || 1;
  const yPad = (Math.max(...allY) - Math.min(...allY)) * 0.05 || 1;
  const xDomain = [Math.min(...allX) - xPad, Math.max(...allX) + xPad];
  const yDomain = [Math.min(...allY) - yPad, Math.max(...allY) + yPad];

  let annotations = [...allLabeled].map(gene => {
    const d = sensitivity[gene];
    if (!d) return null;
    return {
      x: d.tFC, y: d.eFC,
      text: `<b>${gene}</b>`,
      showarrow: true, arrowhead: 0, arrowwidth: 1, arrowcolor: '#aaa',
      ax: 30, ay: -20,
      font: { size: 11, color: '#e8e8e8' },
      bgcolor: 'rgba(22,33,62,0.8)', borderpad: 2,
    };
  }).filter(Boolean);

  annotations = disperseAnnotations(annotations, plotWidth, plotHeight, margin, xDomain, yDomain);

  // Threshold lines
  const shapes = [
    { type: 'line', x0: 0, x1: 0, y0: yDomain[0], y1: yDomain[1], line: { color: '#888', width: 1, dash: 'dash' } },
    { type: 'line', x0: xDomain[0], x1: xDomain[1], y0: 0, y1: 0, line: { color: '#888', width: 1, dash: 'dash' } },
  ];

  const layout = {
    width: plotWidth, height: plotHeight,
    margin,
    paper_bgcolor: '#1a1a2e',
    plot_bgcolor: '#16213e',
    font: { family: 'Inter, system-ui, sans-serif', color: '#e8e8e8' },
    title: {
      text: 'EZH2i Sensitivity Quadrant',
      font: { size: 16 },
    },
    xaxis: {
      title: 'Transformation log\u2082FC (P0 \u2192 P6)',
      gridcolor: '#2a3a5e', zerolinecolor: '#888',
      range: xDomain,
    },
    yaxis: {
      title: 'EZH2i log\u2082FC (P6 \u2192 P6+Taz)',
      gridcolor: '#2a3a5e', zerolinecolor: '#888',
      range: yDomain,
    },
    annotations,
    shapes,
    legend: {
      x: 1, y: 1, xanchor: 'right',
      bgcolor: 'rgba(22,33,62,0.8)',
      font: { size: 11 },
    },
    hovermode: 'closest',
    dragmode: 'zoom',
  };

  return (
    <div className="quadrant-container">
      <div className="quadrant-controls">
        <div className="quadrant-control-group">
          <label>|FC| cutoff</label>
          <input
            type="range" min="0" max="4" step="0.5" value={fcCutoff}
            onChange={e => setFcCutoff(parseFloat(e.target.value))}
          />
          <span>{fcCutoff}</span>
        </div>

        <div className="quadrant-control-group">
          <label>FDR</label>
          <select value={fdrCutoff} onChange={e => setFdrCutoff(parseFloat(e.target.value))}>
            {FDR_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>

        <div className="quadrant-control-group volcano-search">
          <label>Gene</label>
          <div className="search-wrapper">
            <input
              ref={searchRef}
              type="text" value={searchQuery}
              onChange={handleSearchInput}
              onKeyDown={handleSearchKey}
              placeholder="Search gene..."
            />
            {suggestions.length > 0 && (
              <ul className="search-suggestions">
                {suggestions.map(g => (
                  <li key={g} onClick={() => handleSuggestionClick(g)}>{g}</li>
                ))}
              </ul>
            )}
          </div>
          {searchResult && !searchResult.found && (
            <span className="search-miss">Not found in quadrant data</span>
          )}
        </div>

        <div className="volcano-signatures">
          {SIGNATURES.map(sig => (
            <button
              key={sig.panel}
              className={activeSignature?.panel === sig.panel ? 'active' : ''}
              style={{
                borderColor: sig.color,
                backgroundColor: activeSignature?.panel === sig.panel ? sig.color : 'transparent',
              }}
              onClick={() => setActiveSignature(prev => prev?.panel === sig.panel ? null : sig)}
            >
              {sig.label}
            </button>
          ))}
        </div>
      </div>

      <Plot
        data={traces}
        layout={layout}
        config={{ responsive: true, displayModeBar: true, modeBarButtonsToRemove: ['lasso2d', 'select2d'] }}
        onClick={handleClick}
      />
    </div>
  );
}
